import { useEffect, useMemo, useState } from 'react'
import type { ReportType } from '../../stores/reportBuilderStore'
import { useReportBuilderStore } from '../../stores/reportBuilderStore'

interface InferredReportType {
	type: ReportType
	confidence: 'definitive' | 'suggested' | 'needs_clarification'
	reason: string
}

function inferReportType(
	selectedFormIds: string[],
	selectedCycleIds: string[],
	selectedRepeatableIds: string[] | undefined,
): InferredReportType {
	const hasRepeatables = selectedRepeatableIds && selectedRepeatableIds.length > 0
	const hasForms = selectedFormIds.length > 0
	const hasCycles = selectedCycleIds.length > 0
	const cycleCount = selectedCycleIds.length

	// Definitive: raw-data when repeatables are selected
	if (hasRepeatables) {
		return {
			type: 'raw-data',
			confidence: 'definitive',
			reason: 'Repeatable components selected → Raw Data export',
		}
	}

	// Forms selected → form-based report
	if (hasForms) {
		return {
			type: 'form',
			confidence: 'definitive',
			reason: hasCycles ? 'Forms selected with cycles as filters → By Form report' : 'Forms selected → By Form report',
		}
	}

	// Only cycles selected
	if (hasCycles && !hasForms) {
		if (cycleCount === 1) {
			return {
				type: 'cycle',
				confidence: 'definitive',
				reason: 'Single cycle selected → By Cycle report',
			}
		}

		// Multiple cycles, no forms → need clarification
		return {
			type: 'cycle',
			confidence: 'needs_clarification',
			reason: 'Multiple cycles selected. Choose between aggregate or compare.',
		}
	}

	// Default
	return {
		type: 'form',
		confidence: 'suggested',
		reason: 'Select forms or cycles to begin',
	}
}

function SmartDetectionBanner({
	inference,
	onSelectCrossComparison,
}: {
	inference: InferredReportType
	onSelectCrossComparison: (compare: boolean) => void
}) {
	if (inference.confidence === 'needs_clarification') {
		return (
			<div className='p-4 bg-amber-50 border border-amber-200 rounded-lg'>
				<div className='flex items-start gap-3'>
					<div className='flex-shrink-0'>
						<svg
							className='h-5 w-5 text-amber-500'
							fill='none'
							viewBox='0 0 24 24'
							stroke='currentColor'
							aria-hidden='true'>
							<path
								strokeLinecap='round'
								strokeLinejoin='round'
								strokeWidth={2}
								d='M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z'
							/>
						</svg>
					</div>
					<div className='flex-1'>
						<h4 className='text-sm font-semibold text-amber-800'>How would you like to analyze these cycles?</h4>
						<p className='text-sm text-amber-700 mt-1'>
							You've selected multiple cycles. Please choose your analysis type:
						</p>
						<div className='mt-3 flex gap-3'>
							<button
								type='button'
								onClick={() => onSelectCrossComparison(false)}
								className='px-4 py-2 text-sm font-medium rounded-md bg-white border border-amber-300 text-amber-800 hover:bg-amber-100 transition-colors'>
								<span className='font-semibold'>Aggregate</span>
								<span className='block text-xs text-amber-600 mt-0.5'>Combine data from all cycles</span>
							</button>
							<button
								type='button'
								onClick={() => onSelectCrossComparison(true)}
								className='px-4 py-2 text-sm font-medium rounded-md bg-amber-600 text-white hover:bg-amber-700 transition-colors'>
								<span className='font-semibold'>Compare Trends</span>
								<span className='block text-xs text-amber-100 mt-0.5'>Analyze trends across cycles</span>
							</button>
						</div>
					</div>
				</div>
			</div>
		)
	}

	const isActive = inference.confidence === 'definitive' || inference.confidence === 'suggested'
	const bgColor = isActive ? 'bg-blue-50 border-blue-200' : 'bg-neutral-50 border-neutral-200'
	const iconColor = isActive ? 'text-blue-500' : 'text-neutral-400'
	const textColor = isActive ? 'text-blue-800' : 'text-neutral-600'

	const typeLabels: Record<ReportType, string> = {
		form: 'By Form',
		cycle: 'By Cycle',
		'cross-cycle': 'Cross-Cycle Comparison',
		'raw-data': 'Raw Data Export',
		distribution: 'Response Distribution', // V3.3
	}

	return (
		<div className={`p-3 rounded-lg border ${bgColor}`}>
			<div className='flex items-center gap-2'>
				<svg
					className={`h-4 w-4 ${iconColor}`}
					fill='none'
					viewBox='0 0 24 24'
					stroke='currentColor'
					aria-hidden='true'>
					<path
						strokeLinecap='round'
						strokeLinejoin='round'
						strokeWidth={2}
						d='M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z'
					/>
				</svg>
				<span className={`text-sm ${textColor}`}>
					<span className='font-medium'>Auto-detected:</span>{' '}
					<span className='font-semibold'>{typeLabels[inference.type]}</span>
					{inference.confidence === 'definitive' && (
						<span className='ml-2 text-xs text-green-600 bg-green-100 px-1.5 py-0.5 rounded'>✓ Confirmed</span>
					)}
				</span>
			</div>
			<p className={`text-xs mt-1 ml-6 ${textColor} opacity-75`}>{inference.reason}</p>
		</div>
	)
}

export function StepDataSelection() {
	const {
		currentConfig,
		updateConfig,
		forms,
		cycles,
		repeatableComponents,
		isLoadingRepeatables,
		loadRepeatableComponents,
		nextStep,
		validationErrors,
	} = useReportBuilderStore()

	const [formSearch, setFormSearch] = useState('')
	const [cycleSearch, setCycleSearch] = useState('')
	const [repeatableSearch, setRepeatableSearch] = useState('')
	const [userSelectedCrossComparison, setUserSelectedCrossComparison] = useState<boolean | null>(null)

	// Smart inference of report type
	const inference = useMemo(() => {
		const result = inferReportType(
			currentConfig.selectedFormIds,
			currentConfig.selectedCycleIds,
			currentConfig.selectedRepeatableIds,
		)

		// If user has explicitly chosen cross-comparison, override
		if (userSelectedCrossComparison !== null && result.confidence === 'needs_clarification') {
			return {
				type: (userSelectedCrossComparison ? 'cross-cycle' : 'cycle') as ReportType,
				confidence: 'definitive' as const,
				reason: userSelectedCrossComparison
					? 'Cross-cycle comparison mode selected'
					: 'Cycle aggregation mode selected',
			}
		}

		return result
	}, [
		currentConfig.selectedFormIds,
		currentConfig.selectedCycleIds,
		currentConfig.selectedRepeatableIds,
		userSelectedCrossComparison,
	])

	// Auto-update report type when inference changes
	// V3.3.1: Don't overwrite distribution mode - it's explicitly set by user
	useEffect(() => {
		if (currentConfig.reportType === 'distribution') {
			return // Preserve distribution mode
		}
		if (inference.confidence !== 'needs_clarification') {
			if (currentConfig.reportType !== inference.type) {
				updateConfig({ reportType: inference.type })
			}
		}
	}, [inference, currentConfig.reportType, updateConfig])

	// Reset user selection when cycle selection changes significantly
	useEffect(() => {
		if (currentConfig.selectedCycleIds.length < 2) {
			setUserSelectedCrossComparison(null)
		}
	}, [currentConfig.selectedCycleIds.length])

	// Check if we should show repeatables section (when forms are selected and there are repeatables)
	const showRepeatables = useMemo(() => {
		return currentConfig.selectedFormIds.length > 0 && repeatableComponents.length > 0
	}, [currentConfig.selectedFormIds.length, repeatableComponents.length])

	useEffect(() => {
		if (currentConfig.selectedFormIds.length > 0) {
			loadRepeatableComponents(currentConfig.selectedFormIds)
		}
	}, [currentConfig.selectedFormIds, loadRepeatableComponents])

	const filteredForms = useMemo(() => {
		if (!formSearch) return forms
		return forms.filter((f) => f.name.toLowerCase().includes(formSearch.toLowerCase()))
	}, [forms, formSearch])

	const filteredCycles = useMemo(() => {
		if (!cycleSearch) return cycles
		return cycles.filter((c) => c.name.toLowerCase().includes(cycleSearch.toLowerCase()))
	}, [cycles, cycleSearch])

	const filteredRepeatables = useMemo(() => {
		if (!repeatableSearch) return repeatableComponents
		return repeatableComponents.filter(
			(r) =>
				r.label.toLowerCase().includes(repeatableSearch.toLowerCase()) ||
				r.formName.toLowerCase().includes(repeatableSearch.toLowerCase()),
		)
	}, [repeatableComponents, repeatableSearch])

	const toggleForm = (formId: string) => {
		const selected = currentConfig.selectedFormIds.includes(formId)
			? currentConfig.selectedFormIds.filter((id) => id !== formId)
			: [...currentConfig.selectedFormIds, formId]
		updateConfig({ selectedFormIds: selected })

		// Clear repeatables if the form they belong to is deselected
		const removedFormIds = currentConfig.selectedFormIds.filter((id) => !selected.includes(id))
		if (removedFormIds.length > 0) {
			const newRepeatableIds = (currentConfig.selectedRepeatableIds || []).filter(
				(rid) => !repeatableComponents.find((r) => r.id === rid && removedFormIds.includes(r.formId)),
			)
			updateConfig({ selectedRepeatableIds: newRepeatableIds })
		}
	}

	const toggleCycle = (cycleId: string) => {
		const selected = currentConfig.selectedCycleIds.includes(cycleId)
			? currentConfig.selectedCycleIds.filter((id) => id !== cycleId)
			: [...currentConfig.selectedCycleIds, cycleId]
		updateConfig({ selectedCycleIds: selected })
	}

	const toggleRepeatable = (repeatableId: string) => {
		const selected = (currentConfig.selectedRepeatableIds || []).includes(repeatableId)
			? (currentConfig.selectedRepeatableIds || []).filter((id) => id !== repeatableId)
			: [...(currentConfig.selectedRepeatableIds || []), repeatableId]
		updateConfig({ selectedRepeatableIds: selected })
	}

	const selectAllForms = () => {
		const merged = new Set([...currentConfig.selectedFormIds, ...filteredForms.map((f) => f.id)])
		updateConfig({ selectedFormIds: [...merged] })
	}

	const selectAllCycles = () => {
		const merged = new Set([...currentConfig.selectedCycleIds, ...filteredCycles.map((c) => c.id)])
		updateConfig({ selectedCycleIds: [...merged] })
	}

	const selectAllRepeatables = () => {
		const merged = new Set([...(currentConfig.selectedRepeatableIds || []), ...filteredRepeatables.map((r) => r.id)])
		updateConfig({ selectedRepeatableIds: [...merged] })
	}

	const clearForms = () => {
		updateConfig({ selectedFormIds: [], selectedRepeatableIds: [] })
	}

	const clearCycles = () => {
		updateConfig({ selectedCycleIds: [] })
		setUserSelectedCrossComparison(null)
	}

	const clearRepeatables = () => {
		updateConfig({ selectedRepeatableIds: [] })
	}

	const handleCrossComparisonChoice = (compare: boolean) => {
		setUserSelectedCrossComparison(compare)
		updateConfig({ reportType: compare ? 'cross-cycle' : 'cycle' })
	}

	const canProceed = () => {
		// V3.3: Distribution mode - check before inference logic
		if (currentConfig.reportType === 'distribution') {
			return currentConfig.selectedFormIds.length > 0
		}

		// Check if clarification is needed but not provided
		if (inference.confidence === 'needs_clarification') {
			return false
		}

		// Raw data mode
		if (inference.type === 'raw-data') {
			return currentConfig.selectedFormIds.length > 0 && (currentConfig.selectedRepeatableIds || []).length > 0
		}

		// Form mode
		if (inference.type === 'form') {
			return currentConfig.selectedFormIds.length > 0
		}

		// Cycle mode
		if (inference.type === 'cycle') {
			return currentConfig.selectedCycleIds.length > 0
		}

		// Cross-cycle mode
		if (inference.type === 'cross-cycle') {
			return currentConfig.selectedCycleIds.length >= 2
		}

		return false
	}

	const getNextButtonLabel = () => {
		if (inference.type === 'raw-data') {
			return 'Next: Configure Filters'
		}
		return 'Next: Configure Grouping'
	}

	return (
		<div className='space-y-6'>
			<div>
				<h2 className='text-lg font-semibold text-neutral-900 mb-2'>Data Selection</h2>
				<p className='text-sm text-neutral-600'>Select the forms and/or cycles you want to include in your report</p>
			</div>

			{/* Smart Detection Help Box */}
			<div className='p-4 bg-gradient-to-r from-indigo-50 to-blue-50 border border-indigo-200 rounded-lg'>
				<div className='flex items-start gap-3'>
					<div className='flex-shrink-0 mt-0.5'>
						<svg
							className='h-5 w-5 text-indigo-600'
							fill='none'
							viewBox='0 0 24 24'
							stroke='currentColor'
							aria-hidden='true'>
							<path
								strokeLinecap='round'
								strokeLinejoin='round'
								strokeWidth={2}
								d='M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z'
							/>
						</svg>
					</div>
					<div>
						<h3 className='text-sm font-semibold text-indigo-900'>Smart Report Type Detection</h3>
						<p className='text-xs text-indigo-700 mt-1 leading-relaxed'>
							The report type is automatically determined based on your data selection:
						</p>
						<ul className='text-xs text-indigo-600 mt-2 space-y-1'>
							<li className='flex items-center gap-2'>
								<span className='w-1.5 h-1.5 bg-indigo-400 rounded-full' />
								<span>
									<strong>Select forms</strong> → Report by Form (with optional cycle filter)
								</span>
							</li>
							<li className='flex items-center gap-2'>
								<span className='w-1.5 h-1.5 bg-indigo-400 rounded-full' />
								<span>
									<strong>Select cycles only</strong> → Report by Cycle
								</span>
							</li>
							<li className='flex items-center gap-2'>
								<span className='w-1.5 h-1.5 bg-indigo-400 rounded-full' />
								<span>
									<strong>Select 2+ cycles</strong> → Choose between aggregate or trend comparison
								</span>
							</li>
							<li className='flex items-center gap-2'>
								<span className='w-1.5 h-1.5 bg-indigo-400 rounded-full' />
								<span>
									<strong>Select repeatables</strong> → Raw Data export (individual responses)
								</span>
							</li>
						</ul>
					</div>
				</div>
			</div>

			{validationErrors.selection && (
				<div className='p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700'>
					{validationErrors.selection}
				</div>
			)}

			{/* Smart Detection Banner */}
			<SmartDetectionBanner inference={inference} onSelectCrossComparison={handleCrossComparisonChoice} />

			{/* Data Selection Panels */}
			<div className={`grid grid-cols-1 ${showRepeatables ? 'lg:grid-cols-3' : 'lg:grid-cols-2'} gap-6`}>
				{/* Forms Panel */}
				<div
					className={`border rounded-lg ${
						inference.type === 'form' && currentConfig.selectedFormIds.length === 0
							? 'border-red-300'
							: inference.type === 'raw-data' && currentConfig.selectedFormIds.length === 0
								? 'border-red-300'
								: 'border-neutral-200'
					}`}>
					<div className='border-b border-neutral-200 px-4 py-3 bg-neutral-50'>
						<div className='flex items-center justify-between mb-2'>
							<h3 className='font-medium text-neutral-900'>
								Forms ({currentConfig.selectedFormIds.length} selected)
								{(inference.type === 'form' || inference.type === 'raw-data') && (
									<span className='text-red-500 ml-1'>*</span>
								)}
							</h3>
							<div className='space-x-2'>
								<button type='button' onClick={selectAllForms} className='text-xs text-blue-600 hover:text-blue-700'>
									Select All
								</button>
								<button type='button' onClick={clearForms} className='text-xs text-neutral-500 hover:text-neutral-700'>
									Clear
								</button>
							</div>
						</div>
						<input
							type='text'
							placeholder='Search forms...'
							value={formSearch}
							onChange={(e) => setFormSearch(e.target.value)}
							className='w-full px-3 py-1.5 border border-neutral-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500'
						/>
					</div>
					<div className='max-h-64 overflow-y-auto p-2'>
						{filteredForms.length === 0 ? (
							<p className='text-sm text-neutral-500 text-center py-4'>No forms available</p>
						) : (
							<ul className='space-y-1'>
								{filteredForms.map((form) => (
									<li key={form.id}>
										<label className='flex items-center p-2 hover:bg-neutral-50 rounded cursor-pointer'>
											<input
												type='checkbox'
												checked={currentConfig.selectedFormIds.includes(form.id)}
												onChange={() => toggleForm(form.id)}
												className='h-4 w-4 text-blue-600 border-neutral-300 rounded focus:ring-blue-500'
											/>
											<span className='ml-3 text-sm text-neutral-700'>{form.name}</span>
											<span className='ml-auto text-xs text-neutral-400'>{form.cycleIds.length} cycle(s)</span>
										</label>
									</li>
								))}
							</ul>
						)}
					</div>
				</div>

				{/* Repeatables Panel (shown when forms selected and repeatables exist) */}
				{showRepeatables && (
					<div
						className={`border rounded-lg ${
							inference.type === 'raw-data' && (currentConfig.selectedRepeatableIds || []).length === 0
								? 'border-red-300'
								: 'border-neutral-200'
						}`}>
						<div className='border-b border-neutral-200 px-4 py-3 bg-neutral-50'>
							<div className='flex items-center justify-between mb-2'>
								<h3 className='font-medium text-neutral-900'>
									Repeatables ({(currentConfig.selectedRepeatableIds || []).length} selected)
									{inference.type === 'raw-data' && <span className='text-red-500 ml-1'>*</span>}
									<span className='text-xs text-neutral-500 ml-2'>(for Raw Data export)</span>
								</h3>
								<div className='space-x-2'>
									<button
										type='button'
										onClick={selectAllRepeatables}
										className='text-xs text-blue-600 hover:text-blue-700'
										disabled={repeatableComponents.length === 0}>
										Select All
									</button>
									<button
										type='button'
										onClick={clearRepeatables}
										className='text-xs text-neutral-500 hover:text-neutral-700'>
										Clear
									</button>
								</div>
							</div>
							<input
								type='text'
								placeholder='Search repeatables...'
								value={repeatableSearch}
								onChange={(e) => setRepeatableSearch(e.target.value)}
								className='w-full px-3 py-1.5 border border-neutral-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500'
							/>
						</div>
						<div className='max-h-64 overflow-y-auto p-2'>
							{isLoadingRepeatables ? (
								<div className='flex items-center justify-center py-8'>
									<div className='animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600' />
								</div>
							) : filteredRepeatables.length === 0 ? (
								<div className='text-center py-4'>
									<p className='text-sm text-neutral-500'>No repeatable components found</p>
									<p className='text-xs text-neutral-400 mt-1'>
										Repeatables are optional - select them only for Raw Data export
									</p>
								</div>
							) : (
								<ul className='space-y-1'>
									{filteredRepeatables.map((repeatable) => (
										<li key={repeatable.id}>
											<label className='flex items-start p-2 hover:bg-neutral-50 rounded cursor-pointer'>
												<input
													type='checkbox'
													checked={(currentConfig.selectedRepeatableIds || []).includes(repeatable.id)}
													onChange={() => toggleRepeatable(repeatable.id)}
													className='h-4 w-4 mt-0.5 text-blue-600 border-neutral-300 rounded focus:ring-blue-500'
												/>
												<div className='ml-3'>
													<span className='text-sm text-neutral-700'>{repeatable.label}</span>
													<span className='block text-xs text-neutral-400'>
														{repeatable.formName} · {repeatable.childComponents.length} fields
													</span>
												</div>
											</label>
										</li>
									))}
								</ul>
							)}
						</div>
					</div>
				)}

				{/* Cycles Panel */}
				<div
					className={`border rounded-lg ${
						(inference.type === 'cycle' || inference.type === 'cross-cycle') &&
						currentConfig.selectedCycleIds.length === 0
							? 'border-red-300'
							: 'border-neutral-200'
					}`}>
					<div className='border-b border-neutral-200 px-4 py-3 bg-neutral-50'>
						<div className='flex items-center justify-between mb-2'>
							<h3 className='font-medium text-neutral-900'>
								Cycles ({currentConfig.selectedCycleIds.length} selected)
								{(inference.type === 'cycle' || inference.type === 'cross-cycle') && (
									<span className='text-red-500 ml-1'>*</span>
								)}
								{inference.type === 'cross-cycle' && <span className='text-xs text-neutral-500 ml-2'>(min. 2)</span>}
								{(inference.type === 'form' || inference.type === 'raw-data') &&
									currentConfig.selectedFormIds.length > 0 && (
										<span className='text-xs text-neutral-500 ml-2'>(optional filter)</span>
									)}
							</h3>
							<div className='space-x-2'>
								<button type='button' onClick={selectAllCycles} className='text-xs text-blue-600 hover:text-blue-700'>
									Select All
								</button>
								<button type='button' onClick={clearCycles} className='text-xs text-neutral-500 hover:text-neutral-700'>
									Clear
								</button>
							</div>
						</div>
						<input
							type='text'
							placeholder='Search cycles...'
							value={cycleSearch}
							onChange={(e) => setCycleSearch(e.target.value)}
							className='w-full px-3 py-1.5 border border-neutral-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500'
						/>
					</div>
					<div className='max-h-64 overflow-y-auto p-2'>
						{filteredCycles.length === 0 ? (
							<p className='text-sm text-neutral-500 text-center py-4'>No cycles available</p>
						) : (
							<ul className='space-y-1'>
								{filteredCycles.map((cycle) => (
									<li key={cycle.id}>
										<label className='flex items-center p-2 hover:bg-neutral-50 rounded cursor-pointer'>
											<input
												type='checkbox'
												checked={currentConfig.selectedCycleIds.includes(cycle.id)}
												onChange={() => toggleCycle(cycle.id)}
												className='h-4 w-4 text-blue-600 border-neutral-300 rounded focus:ring-blue-500'
											/>
											<span className='ml-3 text-sm text-neutral-700'>{cycle.name}</span>
											<span
												className={`ml-auto text-xs px-2 py-0.5 rounded-full ${
													cycle.status === 'completed'
														? 'bg-green-100 text-green-700'
														: cycle.status === 'active' || cycle.status === 'in-progress'
															? 'bg-blue-100 text-blue-700'
															: 'bg-neutral-100 text-neutral-600'
												}`}>
												{cycle.status}
											</span>
										</label>
									</li>
								))}
							</ul>
						)}
					</div>
				</div>
			</div>

			{/* V3.3: Distribution Analysis Toggle */}
			{currentConfig.selectedFormIds.length > 0 && (
				<div
					className={`p-4 border rounded-lg transition-colors ${
						currentConfig.reportType === 'distribution'
							? 'bg-purple-100 border-purple-300'
							: 'bg-purple-50 border-purple-200'
					}`}>
					<div className='flex items-center justify-between'>
						<div className='flex items-center gap-3'>
							<svg
								className={`h-5 w-5 ${
									currentConfig.reportType === 'distribution' ? 'text-purple-600' : 'text-purple-500'
								}`}
								fill='none'
								viewBox='0 0 24 24'
								stroke='currentColor'
								aria-hidden='true'>
								<path
									strokeLinecap='round'
									strokeLinejoin='round'
									strokeWidth={2}
									d='M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z'
								/>
							</svg>
							<div>
								<h4 className='text-sm font-medium text-purple-900'>Distribution Analysis</h4>
								<p className='text-xs text-purple-700'>
									{currentConfig.reportType === 'distribution'
										? 'Analyzing how responses are distributed for a specific component.'
										: 'Toggle to analyze response distribution across different values.'}
								</p>
							</div>
						</div>
						<label className='relative inline-flex items-center cursor-pointer'>
							<input
								type='checkbox'
								checked={currentConfig.reportType === 'distribution'}
								onChange={(e) => {
									if (e.target.checked) {
										updateConfig({ reportType: 'distribution' })
									} else {
										const inferred = inferReportType(
											currentConfig.selectedFormIds,
											currentConfig.selectedCycleIds,
											currentConfig.selectedRepeatableIds,
										)
										updateConfig({
											reportType: inferred.type,
											distributionComponentId: undefined,
										})
									}
								}}
								className='sr-only peer'
							/>
							<div className="w-11 h-6 bg-neutral-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-purple-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-neutral-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-600" />
						</label>
					</div>
				</div>
			)}

			<div className='flex justify-end'>
				<button
					type='button'
					onClick={nextStep}
					disabled={!canProceed()}
					className='px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed'
					title={
						!canProceed()
							? inference.confidence === 'needs_clarification'
								? 'Please choose between aggregate or compare mode'
								: inference.type === 'raw-data'
									? 'Please select forms and at least one repeatable'
									: inference.type === 'form'
										? 'Please select at least one form'
										: inference.type === 'cross-cycle'
											? 'Please select at least 2 cycles'
											: 'Please select at least one cycle'
							: undefined
					}>
					{getNextButtonLabel()}
				</button>
			</div>
		</div>
	)
}
